import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Alert, Modal, Badge } from 'react-bootstrap';
import axios from 'axios';
import { useAuthUser } from 'react-auth-kit';

const CompanyProfile = () => {
    const authUser = useAuthUser();
    const apiUrl = process.env.REACT_APP_URL;

    const [companyData, setCompanyData] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        industry: '',
        tax_id: '',
        subscription_plan: 'basic',
        max_users: 5,
        logo: null
    });

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    useEffect(() => {
        fetchCompanyData();
    }, []);

    const fetchCompanyData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${apiUrl}/companies/profile`, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`
                }
            });
            setCompanyData(response.data);
        } catch (err) {
            setError('Failed to load company data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const response = await axios.put(`${apiUrl}/companies/profile`, companyData, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`,
                    'Content-Type': 'application/json'
                }
            });

            setSuccess('Company profile updated successfully!');
            setIsEditing(false);
            setCompanyData(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update company profile');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCompanyData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getSubscriptionBadgeVariant = (plan) => {
        switch (plan) {
            case 'basic': return 'secondary';
            case 'premium': return 'primary';
            case 'enterprise': return 'success';
            default: return 'secondary';
        }
    };

    const subscriptionPlans = {
        basic: { name: 'Basic', maxUsers: 5, price: '$29/month' },
        premium: { name: 'Premium', maxUsers: 20, price: '$99/month' },
        enterprise: { name: 'Enterprise', maxUsers: 100, price: '$299/month' }
    };

    if (loading && !companyData.name) {
        return (
            <div className="d-flex justify-content-center p-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="main-content">
            <div className="container-fluid">
                <Row>
                    <Col>
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                            <h4 className="mb-sm-0">Company Profile</h4>
                            <div className="page-title-right">
                                <Badge bg={getSubscriptionBadgeVariant(companyData.subscription_plan)} className="me-2">
                                    {subscriptionPlans[companyData.subscription_plan]?.name || 'Basic'} Plan
                                </Badge>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setShowUpgradeModal(true)}
                                >
                                    Upgrade Plan
                                </Button>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col lg={8}>
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">Company Information</h5>
                                <Button
                                    variant={isEditing ? "secondary" : "primary"}
                                    size="sm"
                                    onClick={() => setIsEditing(!isEditing)}
                                >
                                    {isEditing ? 'Cancel' : 'Edit'}
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {error && <Alert variant="danger">{error}</Alert>}
                                {success && <Alert variant="success">{success}</Alert>}

                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Company Name *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="name"
                                                    value={companyData.name}
                                                    onChange={handleInputChange}
                                                    disabled={!isEditing || loading}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Industry</Form.Label>
                                                <Form.Select
                                                    name="industry"
                                                    value={companyData.industry}
                                                    onChange={handleInputChange}
                                                    disabled={!isEditing || loading}
                                                >
                                                    <option value="">Select Industry</option>
                                                    <option value="logistics">Logistics & Transportation</option>
                                                    <option value="manufacturing">Manufacturing</option>
                                                    <option value="retail">Retail</option>
                                                    <option value="services">Services</option>
                                                    <option value="technology">Technology</option>
                                                    <option value="healthcare">Healthcare</option>
                                                    <option value="finance">Finance</option>
                                                    <option value="other">Other</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Email *</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={companyData.email}
                                                    onChange={handleInputChange}
                                                    disabled={!isEditing || loading}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Phone</Form.Label>
                                                <Form.Control
                                                    type="tel"
                                                    name="phone"
                                                    value={companyData.phone}
                                                    onChange={handleInputChange}
                                                    disabled={!isEditing || loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Website</Form.Label>
                                                <Form.Control
                                                    type="url"
                                                    name="website"
                                                    value={companyData.website}
                                                    onChange={handleInputChange}
                                                    disabled={!isEditing || loading}
                                                    placeholder="https://"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Tax ID</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="tax_id"
                                                    value={companyData.tax_id}
                                                    onChange={handleInputChange}
                                                    disabled={!isEditing || loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Address</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="address"
                                            value={companyData.address}
                                            onChange={handleInputChange}
                                            disabled={!isEditing || loading}
                                        />
                                    </Form.Group>

                                    {isEditing && (
                                        <div className="text-end">
                                            <Button
                                                variant="primary"
                                                type="submit"
                                                disabled={loading}
                                            >
                                                {loading ? 'Saving...' : 'Save Changes'}
                                            </Button>
                                        </div>
                                    )}
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card>
                            <Card.Header>
                                <h5 className="card-title mb-0">Subscription Details</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="text-center mb-3">
                                    <Badge
                                        bg={getSubscriptionBadgeVariant(companyData.subscription_plan)}
                                        className="fs-6 p-2"
                                    >
                                        {subscriptionPlans[companyData.subscription_plan]?.name || 'Basic'} Plan
                                    </Badge>
                                </div>

                                <div className="subscription-details">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Max Users:</span>
                                        <strong>{companyData.max_users}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Price:</span>
                                        <strong>{subscriptionPlans[companyData.subscription_plan]?.price || '$29/month'}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3">
                                        <span>Status:</span>
                                        <Badge bg="success">Active</Badge>
                                    </div>

                                    <div className="d-grid">
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => setShowUpgradeModal(true)}
                                        >
                                            Manage Subscription
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card className="mt-3">
                            <Card.Header>
                                <h5 className="card-title mb-0">Quick Stats</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Active Users:</span>
                                    <strong>3/{companyData.max_users}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Storage Used:</span>
                                    <strong>2.3 GB</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span>API Calls:</span>
                                    <strong>1,245 this month</strong>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Upgrade Plan Modal */}
                <Modal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Subscription Plans</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            {Object.entries(subscriptionPlans).map(([planKey, plan]) => (
                                <Col md={4} key={planKey} className="mb-3">
                                    <Card className={`h-100 ${companyData.subscription_plan === planKey ? 'border-primary' : ''}`}>
                                        <Card.Body className="text-center">
                                            <h5 className="card-title">{plan.name}</h5>
                                            <div className="fs-3 text-primary mb-2">{plan.price}</div>
                                            <p className="text-muted">Up to {plan.maxUsers} users</p>

                                            {companyData.subscription_plan === planKey ? (
                                                <Badge bg="success" className="w-100 p-2">Current Plan</Badge>
                                            ) : (
                                                <Button
                                                    variant={planKey === 'enterprise' ? 'success' : 'primary'}
                                                    className="w-100"
                                                    onClick={() => {
                                                        // Here you would handle plan upgrade
                                                        console.log(`Upgrade to ${plan.name}`);
                                                        setShowUpgradeModal(false);
                                                    }}
                                                >
                                                    {planKey === 'basic' ? 'Downgrade' : 'Upgrade'}
                                                </Button>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Modal.Body>
                </Modal>
            </div>
        </div>
    );
};

export default CompanyProfile;
